/**
 * Applications-list version display helpers.
 *
 * Uploaded binaries are analyzed under a synthetic SemVer-2.0 identity of the form
 * `<manifestVersion>+sha256.<first16>` (built by device-service's buildSyntheticBinaryVersion).
 * eval-service preserves that value as the row's identity `version`/`installedVersion` and exposes a
 * separate clean `displayVersion` (manifest version) for the UI. Store/managed apps have no synthetic
 * suffix and no `manifestVersion`, so `displayVersion` equals their normal version.
 *
 * These helpers let the table show the clean version for uploaded binaries only, leaving store and
 * managed apps (and the substitute-version indicator) exactly as they were.
 */

/** SemVer build-metadata marker that identifies an uploaded-binary synthetic version. */
export const SYNTHETIC_BINARY_VERSION_MARKER = '+sha256.';

/** True when a version string is an uploaded-binary synthetic identity (`<ver>+sha256.<hash>`). */
export const isSyntheticBinaryVersion = (version?: string): boolean =>
  typeof version === 'string' && version.includes(SYNTHETIC_BINARY_VERSION_MARKER);

/**
 * Resolves the version string shown in the Applications list for a row.
 *
 * - Uploaded binaries (synthetic installed version): show the clean `displayVersion`.
 * - Managed apps with installed version "0": fall back to the analyzed version (existing behaviour).
 * - Everything else: show the installed version (existing behaviour).
 */
export const getDisplayedAppVersion = ({
  installedVersion,
  analyzedVersion,
  displayVersion,
}: {
  installedVersion?: string;
  analyzedVersion?: string;
  displayVersion?: string;
}): string | undefined => {
  if (isSyntheticBinaryVersion(installedVersion)) {
    return displayVersion || analyzedVersion || installedVersion;
  }
  return installedVersion === '0' ? analyzedVersion : installedVersion;
};
